import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type PlaygroundHeaderState = {
  routeId: string | undefined;
  title: string;
};

type PlaygroundHeaderContextValue = {
  headerState: PlaygroundHeaderState | null;
  publishHeaderState: (state: PlaygroundHeaderState) => void;
};

const PlaygroundHeaderContext =
  createContext<PlaygroundHeaderContextValue | null>(null);

export const PlaygroundHeaderProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [publishedState, setPublishedState] =
    useState<PlaygroundHeaderState | null>(null);
  const publishHeaderState = useCallback((state: PlaygroundHeaderState) => {
    setPublishedState((current) =>
      current !== null &&
      current.routeId === state.routeId &&
      current.title === state.title
        ? current
        : state,
    );
  }, []);
  const value = useMemo(
    () => ({ headerState: publishedState, publishHeaderState }),
    [publishedState, publishHeaderState],
  );

  return (
    <PlaygroundHeaderContext.Provider value={value}>
      {children}
    </PlaygroundHeaderContext.Provider>
  );
};

export const usePlaygroundHeaderState = () =>
  useContext(PlaygroundHeaderContext)?.headerState ?? null;

export const usePublishPlaygroundHeaderState = (
  routeId: string | undefined,
  title: string,
) => {
  const publishHeaderState = useContext(
    PlaygroundHeaderContext,
  )?.publishHeaderState;

  useLayoutEffect(() => {
    publishHeaderState?.({ routeId, title });
  }, [publishHeaderState, routeId, title]);
};
